/**
 * A row of equal-width pills, one selected — "Lay pages out" in the screenshots.
 *
 * NOT THE SAME CONTROL AS `SegmentedControl`, though they look related. A segmented control is
 * a single track with a sliding selection and reads as one setting with N positions; these are
 * separate bordered buttons that each carry an icon and can be selected on their own. pdf uses
 * the segmented one for "How should we bring it in?" and these for the layout, and the
 * difference is legible on screen, so copying both is cheaper than arguing one into the other.
 *
 * Equal width with `flex: 1`, and labels truncate rather than wrap — two pills of different
 * heights because one label is a word longer is the thing this shape is avoiding.
 */

import { Box } from '@chakra-ui/react';
import type { ReactNode } from 'react';

export interface PillOption<T extends string | number> {
  value: T;
  label: string;
  /** A small leading glyph. The screenshots use lucide's ArrowRightLeft and ArrowDownUp. */
  icon?: ReactNode;
  /** Usually a `<TierBadge />`. */
  badge?: ReactNode;
}

export interface PillGroupProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: PillOption<T>[];
}

export function PillGroup<T extends string | number>({
  value,
  onChange,
  options,
}: PillGroupProps<T>) {
  return (
    /*
     * Wraps rather than squeezing. Two pills fit a 460px panel comfortably; a third turned
     * "Side by side" into "Side by si…", because every pill is flex:1 over a zero min-width
     * and the label truncates before the row does. A minimum wide enough for the longest
     * label sends the overflow to a second line instead, where it is still readable.
     */
    <Box display="flex" flexWrap="wrap" gap="1.5">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Box
            as="button"
            key={String(option.value)}
            onClick={() => onChange(option.value)}
            aria-pressed={active}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="1.5"
            flex="1 1 0"
            minW="112px"
            py="1.5"
            px="2"
            borderRadius="md"
            fontSize="2xs"
            fontWeight="500"
            borderWidth="1px"
            borderColor={active ? 'figma.accent' : 'figma.border'}
            bg={active ? 'figma.accentMuted' : 'transparent'}
            color={active ? 'figma.accent' : 'figma.textSecondary'}
            transition="all 0.15s"
            _hover={{ borderColor: active ? 'figma.accent' : 'figma.borderStrong' }}
          >
            {option.icon}
            <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {option.label}
            </Box>
            {option.badge}
          </Box>
        );
      })}
    </Box>
  );
}
